import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { adminApi, errMsg, imgUrl } from "@/lib/api";
import { PageTitle, Btn, Pill } from "@/admin/ui";
import { ImageField } from "@/admin/ProductDialogs";

const EMPTY = { title: "", text: "", image: "", cta: "Shop now", link_type: "product", link_id: "", start_date: "", end_date: "", active: true, sort: 0 };
const F = ({ label, children }) => <div className="space-y-1"><Label className="text-xs font-bold uppercase text-stone-500">{label}</Label>{children}</div>;

const BannerDialog = ({ banner, products, cats, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...banner });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const targets = f.link_type === "product" ? products.map((p) => [p.id, `${p.name} (${p.unit})`]) : cats.map((c) => [c.id, c.name]);
  const save = async () => {
    const body = { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]])), sort: parseInt(f.sort, 10) || 0 };
    try {
      banner.id ? await adminApi.put(`/banners/${banner.id}`, body) : await adminApi.post("/banners", body);
      toast.success("Banner saved"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-md max-h-[90vh] overflow-y-auto" data-testid="banner-dialog">
        <DialogHeader><DialogTitle>{banner.id ? "Edit banner" : "New banner"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><F label="Title"><Input value={f.title} onChange={set("title")} data-testid="banner-title-input" /></F></div>
          <div className="col-span-2"><F label="Text"><Input value={f.text} onChange={set("text")} data-testid="banner-text-input" /></F></div>
          <F label="Button text"><Input value={f.cta} onChange={set("cta")} data-testid="banner-cta-input" /></F>
          <F label="Sort"><Input type="number" value={f.sort} onChange={set("sort")} data-testid="banner-sort-input" /></F>
          <F label="Links to">
            <select value={f.link_type} onChange={(e) => setF({ ...f, link_type: e.target.value, link_id: "" })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="banner-link-type">
              <option value="product">Product / combo</option><option value="category">Category</option>
            </select>
          </F>
          <F label="Target">
            <select value={f.link_id} onChange={set("link_id")} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="banner-link-target">
              <option value="">Choose…</option>
              {targets.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
            </select>
          </F>
          <F label="Show from (optional)"><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="banner-start-input" /></F>
          <F label="Show until (optional)"><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="banner-end-input" /></F>
          <div className="col-span-2"><F label="Photo"><ImageField value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="banner-image" /></F></div>
          <label className="col-span-2 flex items-center gap-2 text-sm font-semibold"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="banner-active-switch" /> Show on storefront</label>
        </div>
        <div className="flex justify-end gap-2 mt-2"><Btn variant="outline" onClick={onClose}>Cancel</Btn><Btn disabled={!f.title || !f.link_id} onClick={save} data-testid="banner-save-btn">Save</Btn></div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => adminApi.get("/banners").then((r) => setBanners(r.data));
  useEffect(() => {
    load();
    adminApi.get("/products").then((r) => setProducts(r.data));
    adminApi.get("/categories").then((r) => setCats(r.data));
  }, []);
  const name = (b) => (b.link_type === "product" ? products.find((p) => p.id === b.link_id)?.name : cats.find((c) => c.id === b.link_id)?.name) || "—";
  const toggle = async (b, v) => { await adminApi.put(`/banners/${b.id}`, { ...b, active: v }); load(); };
  const del = async (b) => { if (window.confirm(`Delete banner "${b.title}"?`)) { await adminApi.delete(`/banners/${b.id}`); load(); } };

  return (
    <div className="max-w-4xl">
      <PageTitle sub="Seasonal banners on the home page. Use dates to show them only during a festival." action={<Btn onClick={() => setDlg({})} data-testid="add-banner-btn"><Plus className="w-4 h-4 inline mr-1" />Add banner</Btn>}>Festival banners</PageTitle>
      <div className="bg-white border border-stone-200 rounded-md divide-y divide-stone-100">
        {banners.length === 0 && <p className="p-6 text-sm text-stone-500">No banners yet.</p>}
        {banners.map((b) => (
          <div key={b.id} className="flex items-center gap-3 p-3" data-testid={`admin-banner-${b.id}`}>
            {b.image ? <img src={imgUrl(b.image)} alt="" className="w-24 h-14 rounded object-cover" /> : <div className="w-24 h-14 rounded bg-stone-100" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{b.title}</p>
              <p className="text-xs text-stone-500">→ {name(b)} · {b.start_date || b.end_date ? `${b.start_date || "…"} to ${b.end_date || "…"}` : "No date limit"}</p>
            </div>
            <Pill tone={b.active ? "green" : "grey"}>{b.active ? "Live" : "Off"}</Pill>
            <Switch checked={b.active} onCheckedChange={(v) => toggle(b, v)} data-testid={`banner-toggle-${b.id}`} />
            <button onClick={() => setDlg(b)} className="p-1.5 text-stone-500 hover:text-primary" data-testid={`edit-banner-${b.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(b)} className="p-1.5 text-stone-500 hover:text-accent" data-testid={`delete-banner-${b.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {dlg && <BannerDialog banner={dlg} products={products} cats={cats} onClose={() => setDlg(null)} onSaved={load} />}
    </div>
  );
}
